import { Download, Loader2, Receipt, RotateCw } from "lucide-react";
import { useState } from "react";
import { downloadRrhhPayroll } from "../api/rrhh";
import type { RrhhPayroll } from "../types/rrhh";

type Props = {
  payrolls: RrhhPayroll[];
  loading: boolean;
  onReload: () => Promise<void> | void;
};

export function PayrollsPanel({ payrolls, loading, onReload }: Props) {
  const [descargando, setDescargando] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function descargar(id: number) {
    setDescargando(id);
    setError(null);
    try {
      await downloadRrhhPayroll(id);
    } catch {
      // Antes fallaba en silencio (promesa rechazada sin capturar) y parecía
      // que el botón no hacía nada.
      setError("No se ha podido descargar la nómina. Inténtalo de nuevo o avisa a administración.");
    } finally {
      setDescargando(null);
    }
  }

  return (
    <DocumentsShell
      title="Nóminas"
      subtitle="Descarga tus recibos mensuales."
      icon={<Receipt size={22} />}
      action={
        <button
          type="button"
          onClick={() => void onReload()}
          disabled={loading}
          className="rrhh-soft-button"
        >
          <RotateCw size={15} className={loading ? "animate-spin" : ""} />
          Actualizar
        </button>
      }
    >
      {loading ? (
        <EmptyText text="Cargando nóminas..." />
      ) : payrolls.length === 0 ? (
        <EmptyText text="No hay nóminas disponibles." />
      ) : (
        <div className="space-y-3">
          {error && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-2.5 text-sm font-semibold text-rose-700">
              {error}
            </div>
          )}
          {payrolls.map((payroll) => (
            <div
              key={payroll.id}
              className="rrhh-document-row flex items-center justify-between gap-4"
            >
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <span className="rrhh-document-icon">
                  <Receipt size={19} />
                </span>
                <div className="min-w-0">
                  <div className="truncate font-black text-slate-900">
                    {payrollLabel(payroll)}
                  </div>
                  <div className="truncate text-sm font-semibold text-slate-500">
                    {payroll.fecha_subida
                      ? `Disponible desde el ${new Date(payroll.fecha_subida).toLocaleDateString("es-ES")}`
                      : "Documento disponible"}
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => void descargar(payroll.id)}
                disabled={descargando === payroll.id}
                className="rrhh-primary-button shrink-0 disabled:opacity-60"
              >
                {descargando === payroll.id ? (
                  <Loader2 size={17} className="animate-spin" />
                ) : (
                  <Download size={17} />
                )}
                {descargando === payroll.id ? "Descargando..." : "Descargar"}
              </button>
            </div>
          ))}
        </div>
      )}
    </DocumentsShell>
  );
}

function payrollLabel(payroll: RrhhPayroll) {
  if (payroll.tipo === "extra_julio") return `Extra Julio ${payroll.anio}`;
  if (payroll.tipo === "extra_diciembre")
    return `Extra Diciembre ${payroll.anio}`;

  return `${monthName(payroll.mes)} ${payroll.anio}`;
}

function monthName(month: number) {
  const months = [
    "Enero",
    "Febrero",
    "Marzo",
    "Abril",
    "Mayo",
    "Junio",
    "Julio",
    "Agosto",
    "Septiembre",
    "Octubre",
    "Noviembre",
    "Diciembre",
  ];

  return months[month - 1] ?? "Mes";
}

function DocumentsShell({
  title,
  subtitle,
  icon,
  action,
  children,
}: {
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="rrhh-panel-header">
        <div className="relative flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <span className="rrhh-panel-icon">{icon}</span>

            <div>
            <div className="text-xs font-black uppercase tracking-[0.18em] text-emerald-600">
              Documentos salariales
            </div>

            <h2 className="mt-1 text-2xl font-black tracking-tight text-slate-950">
              {title}
            </h2>

            <p className="mt-1 text-sm font-semibold leading-6 text-slate-500">
              {subtitle}
            </p>
            </div>
          </div>

          {action}
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto p-5">{children}</div>
    </div>
  );
}

function EmptyText({ text }: { text: string }) {
  return <div className="rrhh-empty-state">{text}</div>;
}

